<template>
  <div v-if='showExpressCheckout' id='payment-request-element-wrapper'>
    <fieldset>
      <legend>Express Checkout</legend>
      <div id='payment-request-element'>
        <button aria-label="Apple Pay" v-if='applePay' @click.prevent='initiate_express_checkout' class='apple-pay-button express-checkout-button'></button>
        <div id='google-pay-container'></div>
      </div>
    </fieldset>
    <div class='payment-request-separator'>
      <div></div>
      <div>or</div>
      <div></div>
    </div>
  </div>
</template>

<script>
  import * as Sentry from '@sentry/browser'
import { loadStripe } from '@stripe/stripe-js'
import ShippingMethodService from 'app/helpers/ShippingMethodService'
import LineItemsMixin from 'app/mixins/LineItemsMixin'
import TotalsMixin from 'app/mixins/TotalsMixin'
import remove from 'lodash/remove'
import some from 'lodash/some'
import Vue from "vue"

  export default {
    mixins: [LineItemsMixin, TotalsMixin],
    props: [
      'preorder',
      'order',
      'estimated_delivery',
      'express_checkout',
      'express_checkout_data',
      'prices',
      'hide_pro',
      'did_initiate_express_checkout',
      'did_complete_express_checkout',
      'did_cancel_express_checkout',
      'shipping_address_did_change'
    ],
    async mounted() {
      this.stripe = await loadStripe(window.STRIPE_PUBLISHABLE_KEY)
      const elements = this.stripe.elements()

      const shippingMethodService = new ShippingMethodService(this.$http)

      // setup the initial payment reqeust
      this.paymentRequest = this.stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: 'Order total',
          amount: 0,
        },

        displayItems: [],
        requestPayerName: true,
        requestPayerEmail: true,
        requestPayerPhone: true,
        requestShipping: true,
        disableWallets: ['link']
      })

      // listen for shipping address changes, so we can fetch and
      // popoulate the shipping options
      this.paymentRequest.on('shippingaddresschange', async (ev) => {
        // ev.shippingAddress has the following format
        // {
        //   "addressLine": [],
        //   "country": "US",
        //   "postalCode": "63119",
        //   "recipient": "",
        //   "region": "MO",
        //   "city": "Saint Louis",
        //   "phone": "",
        //   "sortingCode": "",
        //   "dependentLocality": "",
        //   "organization": ""
        // }

        let formattedShippingOptions = [{
          id: '0',
          label: 'Standard Shipping',
          detail: "",
          amount: Math.round(this.default_shipping_for_country(ev.shippingAddress.country || 'US') * 100)
        }]

        // console.log(`shipping address changed: ${JSON.stringify(ev.shippingAddress)}`)
        const newShippingOptions = await shippingMethodService.updateAddress(ev.shippingAddress)
        formattedShippingOptions = newShippingOptions.map(shippingMethod => {
          return {
            id: shippingMethod.id.toString(),
            label: shippingMethod.name,
            detail: this.preorder ? `Estimated delivery by ${this.estimated_delivery}` : shippingMethodService.estimated_delivery_text(shippingMethod),
            amount: shippingMethod.price_cents
          }
        })

        // we need to set the first shipping method as the selected one
        // on the order, since this will appear to be correct
        // even if the user doesn't select it
        if ((this.order.shipping_method == null || !some(newShippingOptions, ['id', this.order.shipping_method.id])) && newShippingOptions.length > 0) {
          const shippingMethod = shippingMethodService.find(newShippingOptions[0].id)
          this.order.shipping_method = shippingMethod
        } else if (this.order.shipping_method != null && some(newShippingOptions, ['id', this.order.shipping_method.id])) {
          // otherwise, if they've already selected a shipping option, we need to make that
          // the first one in the list
          let selectedOption = null
          formattedShippingOptions.forEach((formattedOption) => {
            if (parseInt(formattedOption.id) == this.order.shipping_method.id) {
              selectedOption = formattedOption
            }
          })

          if (selectedOption) {
            remove(formattedShippingOptions, (option) => (option == selectedOption))
            formattedShippingOptions.unshift(selectedOption)
          }
        }

        // notify observers that shipping address changed
        // since this may (will) call async, await the result
        await this.shipping_address_changed(ev.shippingAddress)

        if (formattedShippingOptions.length == 0) {
          ev.updateWith({status: 'invalid_shipping_address'});
        } else {
          ev.updateWith({
            status: 'success',
            shippingOptions: this.preorder ? [formattedShippingOptions[0]] : formattedShippingOptions,
            ...this.orderDetails
          })
        }
      })

      // watch for shipping option changes and update the order/totals
      this.paymentRequest.on('shippingoptionchange', (ev) => {
        // ev
        // {
        //   "shippingOption": {
        //     "amount": 3500,
        //     "label": "Express Shipping",
        //     "id": "257",
        //     "detail": "Guaranteed delivery by December 22"
        //   }
        // }

        if (ev.shippingOption) {
          const shippingMethod = shippingMethodService.find(ev.shippingOption.id)
          this.order.shipping_method = shippingMethod

          ev.updateWith({
            status: 'success',
            ...this.orderDetails
          })
        } else {
          ev.updateWith({
            status: 'fail'
          })
        }
      })

      // handle the cancel event, which can come in after the payment has gone through
      this.paymentRequest.on('cancel', (ev) => {
        this.cancel_express_checkout()
      })

      // handle the payment response
      // Need this to update the order (shipping, card info) and proceed
      // to the thank you page
      this.paymentRequest.on('token', async (ev) => {
        try {
          await this.complete_express_checkout(ev)
        } catch(errorStatus) {
          if (errorStatus == 400 || errorStatus == 422) {
            // invalid address
            ev.complete('invalid_shipping_address')
          } else {
            // 500 or something else
            // report to Sentry
            Sentry.addBreadcrumb({
              message: '[CAUGHT] error with payment request button'
            });
            Sentry.captureException(new Error('broken: error status – ' + errorStatus));
            ev.complete('fail')
          }
        }
      });

      // check for wallet availability before showing the request button
      (async () => {
        // Check the availability of the Payment Request API first.
        const result = await this.paymentRequest.canMakePayment();
        if (result) {
          this.applePay = result.applePay
          this.googlePay = result.googlePay
        }

        if (this.googlePay) {
          const prButton = elements.create('paymentRequestButton', {
            paymentRequest: this.paymentRequest,
          });

          prButton.on('click', (ev) => {
            if (this.did_initiate_express_checkout instanceof Function) {
              // check if the parent is ok to proceed, preventDefault if not
              if (!this.did_initiate_express_checkout()) {
                // prevent default so we can validate form
                ev.preventDefault()
              }
            }
          })

          // do this is a new render cycle
          // because the DOM gets updated once this.googlePay is true
          Vue.nextTick(() => {
            prButton.mount('#google-pay-container')
          });
        }
      })();
    },
    data() {
      return {
        applePay: false,
        googlePay: false,
        errors: {},
        paymentRequest: null
      }
    },
    computed: {
      card_error() {
        if (this.errors.card === 'Your postal code is incomplete.') {
          return "Payment method postal code is required (located directly above after your card's CVC)."
        } else {
          return this.errors.card
        }
      },
      orderDetails() {
        const details = {
          total: {
            label: 'Order total',
            amount: Math.round(this.raw_total * 100),
          },
        }

        // update the display line items
        const displayItems = []
        this.line_items.forEach((lineItem) => {
          let label = lineItem.name
          if (lineItem.quantity > 1) {
            label = `${label} (${lineItem.quantity})`
          }

          displayItems.push({
            label: label,
            amount: Math.round(lineItem.total * 100)
          })
        })

        // show any discount
        if (this.calculated_promo_code_discount > 0) {
          displayItems.push({
            label: 'Discount',
            amount: Math.round(this.calculated_promo_code_discount * -100)
          })
        }

        // add shipping
        if (this.shipping > 0) {
          displayItems.push({
            label: 'Shipping',
            amount: Math.round(this.shipping * 100)
          })
        }

        // add sales tax
        if (this.order.tax_rate != null) {
          displayItems.push({
            label: this.tax_label,
            amount: Math.round(this.calculated_tax * 100)
          })
        }

        details.displayItems = displayItems
        return details
      },
      showExpressCheckout() {
        return this.applePay || this.googlePay
      },
      buttonText() {
        if (this.applePay) return 'Apple Pay'
        if (this.googlePay) return 'Google Pay'
      }
    },
    watch: {
      order: {
        handler() {
          if (!this.paymentRequest) {
            return
          }

          // we can't update this object while the browser UI is showing
          if (this.paymentRequest.isShowing()) {
            return
          }

          this.paymentRequest.update(this.orderDetails)
        },
        deep: true,
        immediate: false,
      }
    },
    methods: {
      initiate_express_checkout() {
        if (this.did_initiate_express_checkout instanceof Function) {
          // have the parent do whatever it needs to do
          // it should return true if everything is ok to continue
          // or false if not
          if (this.did_initiate_express_checkout()) {
            this.paymentRequest.show()
          }
        }
      },
      shipping_address_changed(shippingAddress) {
        if (this.shipping_address_did_change instanceof Function) {
          // shipping_address_did_change should return a Promise
          return this.shipping_address_did_change(shippingAddress)
        } else {
          return Promise.resolve(true)
        }
      },
      cancel_express_checkout() {
        if (this.did_cancel_express_checkout instanceof Function) {
          this.did_cancel_express_checkout()
        }
      },
      complete_express_checkout(event) {
        if (this.did_complete_express_checkout instanceof Function) {
          return this.did_complete_express_checkout(event)
        } else {
          return Promise.resolve({})
        }
      }
    }
  }
</script>
